import React, { useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, TextField, Button, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";

function CustomEditableTable({ initialData, onTableChange }) {
  const [rows, setRows] = useState(initialData);
  const [editIdx, setEditIdx] = useState(-1);
  const [editRow, setEditRow] = useState({});
  const [addColOpen, setAddColOpen] = useState(false);
  const [newColName, setNewColName] = useState("");

  const startEdit = (idx) => {
    setEditIdx(idx);
    setEditRow({ ...rows[idx] });
  };

  const cancelEdit = () => {
    setEditIdx(-1);
    setEditRow({});
  };

  const saveEdit = (idx) => {
    const updatedRows = rows.map((row, i) => (i === idx ? editRow : row));
    setRows(updatedRows);
    setEditIdx(-1);
    setEditRow({});
  };

  const handleChange = (e, field) => {
    setEditRow({ ...editRow, [field]: e.target.value });
  };

  const handleAddRow = () => {
    if (rows.length === 0) return;
    const emptyRow = {};
    Object.keys(rows[0]).forEach(col => {
      emptyRow[col] = "";
    });
    setRows([...rows, emptyRow]);
  };

  const handleRemoveRow = (idx) => {
    setRows(rows.filter((_, i) => i !== idx));
    if (editIdx === idx) {
      cancelEdit();
    }
  };

  const handleAddColOpen = () => {
    setNewColName("");
    setAddColOpen(true);
  };

  const handleAddCol = () => {
    if (!newColName.trim()) return;
    setRows(rows.map(row => ({ ...row, [newColName]: "" })));
    setAddColOpen(false);
    setNewColName("");
  };

  const handleRemoveCol = (col) => {
    if (Object.keys(rows[0]).length === 1) return;
    setRows(rows.map(row => {
      const newRow = { ...row };
      delete newRow[col];
      return newRow;
    }));
  };

  const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

  // Call onTableChange whenever rows change
  React.useEffect(() => {
    if (onTableChange) {
      onTableChange(rows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Custom Editable Table</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button variant="contained" color="primary" onClick={handleAddRow}>
          Add Row
        </Button>
        <Button variant="outlined" color="primary" onClick={handleAddColOpen}>
          Add Column
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col, idx) => (
                <TableCell key={idx} sx={{ position: 'relative' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {col}
                    {columns.length > 1 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveCol(col)}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              ))}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                {columns.map((field, fidx) => (
                  <TableCell key={fidx}>
                    {editIdx === idx ? (
                      <TextField
                        value={editRow[field]}
                        onChange={e => handleChange(e, field)}
                        size="small"
                      />
                    ) : (
                      row[field]
                    )}
                  </TableCell>
                ))}
                <TableCell>
                  {editIdx === idx ? (
                    <>
                      <IconButton onClick={() => saveEdit(idx)} color="primary"><SaveIcon /></IconButton>
                      <IconButton onClick={cancelEdit} color="secondary"><CancelIcon /></IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton onClick={() => startEdit(idx)} color="primary"><EditIcon /></IconButton>
                      <IconButton onClick={() => handleRemoveRow(idx)} color="error"><DeleteIcon /></IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={addColOpen} onClose={() => setAddColOpen(false)}>
        <DialogTitle>Add Column</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Column Name"
            fullWidth
            value={newColName}
            onChange={e => setNewColName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddColOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCol} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CustomEditableTable; 